/**
 * Language codes a device can report that our translations store under a
 * different code.
 *
 * Norwegian is stored as `no` in the i18n service and in every locale file,
 * while iOS and Android report the written standard instead: `nb` (Bokmål) or
 * `nn` (Nynorsk). Nynorsk maps to `no` too, since Norwegian readers read Bokmål
 * and the only alternative is English.
 *
 * A `Map` rather than an object literal: the code comes from outside (the
 * native app, or `?lng=` on the detector path), and an object lookup would
 * also hit inherited properties, turning `constructor` into the `Object`
 * function.
 */
const LANGUAGE_ALIASES = new Map<string, string>([
  ['nb', 'no'],
  ['nn', 'no'],
]);

/**
 * Normalizes a language code from the native app or the browser to the bare
 * ISO-639-1 code our translations are keyed by: lower-cased, without a region
 * subtag (`de-AT`, `en_GB`), and with device-specific codes mapped onto the
 * stored one (`nb`/`nn` → `no`, see {@link LANGUAGE_ALIASES}).
 *
 * Use it wherever a language enters i18next, so every app resolves the same
 * code for the same device.
 *
 * @param language - The language code as reported, e.g. `nb-NO`.
 * @returns The normalized code, e.g. `no`.
 */
const normalizeLanguage = (language: string): string => {
  const code = language.toLowerCase().split(/[-_]/)[0];

  return LANGUAGE_ALIASES.get(code) ?? code;
};

export default normalizeLanguage;
